// i18n
import { useTranslation } from 'react-i18next';

// types
import {
	Account,
	Category,
	Component,
	ExpenseType,
	Loan,
	Tag,
	TransactionType,
} from '../../../@types';

// mui
import { Grid, Typography } from '@mui/material';

// ui
import { IconCircle } from '../IconCircle/IconCircle';
import { TableInfo, TableInfoProps } from '../TableInfo/TableInfo';

// utils
import { translateTransactionType } from '../../../utils/translations';

export interface TableInfoParamsProps {
	amountMax?: string;
	amountMin?: string;
	categories?: Category[];
	components?: Component[];
	currency?: string;
	dateEnd?: string;
	dateStart?: string;
	expenseTypes?: ExpenseType[];
	fromAccount?: Account;
	groupBy?: string[];
	loan?: Loan;
	tags?: Tag[];
	types?: TransactionType[];
}

export const TableInfoParams = ({
	amountMax,
	amountMin,
	categories = [],
	components = [],
	currency,
	dateEnd,
	dateStart,
	expenseTypes = [],
	fromAccount,
	groupBy,
	loan,
	tags = [],
	types,
}: TableInfoParamsProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const data: TableInfoProps['data'] = [];

	if (loan) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="loan" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('financingSource')}</Typography>
					</Grid>
				</Grid>
			),
			value: loan?.code,
		});
	}

	if (fromAccount) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="account" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('account')}</Typography>
					</Grid>
				</Grid>
			),
			value: fromAccount?.name,
		});
	}

	if (types) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="payment" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('transactionType')}</Typography>
					</Grid>
				</Grid>
			),
			value: (
				<Grid container direction="column" spacing={1}>
					{types.map((type, i) => {
						return (
							<Grid item key={`txType-${i}`}>
								<Typography color="brand.grey.500">{translateTransactionType(type, t)}</Typography>
							</Grid>
						);
					})}
				</Grid>
			),
		});
	}

	if (groupBy) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="expandMore" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('groupBy')}</Typography>
					</Grid>
				</Grid>
			),
			value: (
				<Grid container direction="column" spacing={1}>
					{groupBy.map((group, i) => {
						return (
							<Grid item key={`groupBy-${i}`}>
								<Typography color="brand.grey.500">{`${t(String(i + 1))}. ${t(group)}`}</Typography>
							</Grid>
						);
					})}
				</Grid>
			),
		});
	}

	if (components.length) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="component" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('component')}</Typography>
					</Grid>
				</Grid>
			),
			value: (
				<Grid container direction="column" spacing={1}>
					{components.map((component, i) => {
						return (
							<Grid item key={`component-${i}`}>
								<Typography color="brand.grey.500">{component.name}</Typography>
							</Grid>
						);
					})}
				</Grid>
			),
		});
	}

	if (categories.length) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="category" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('category')}</Typography>
					</Grid>
				</Grid>
			),
			value: (
				<Grid container direction="column" spacing={1}>
					{categories.map((category, i) => {
						return (
							<Grid item key={`category-${i}`}>
								<Typography color="brand.grey.500">{category.name}</Typography>
							</Grid>
						);
					})}
				</Grid>
			),
		});
	}

	if (expenseTypes.length) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="expenseType" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('expenseType')}</Typography>
					</Grid>
				</Grid>
			),
			value: (
				<Grid container direction="column" spacing={1}>
					{expenseTypes.map((expenseType, i) => {
						return (
							<Grid item key={`expense-type-${i}`}>
								<Typography color="brand.grey.500">{expenseType.name}</Typography>
							</Grid>
						);
					})}
				</Grid>
			),
		});
	}

	if (tags.length) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="tag" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('tags')}</Typography>
					</Grid>
				</Grid>
			),
			value: (
				<Grid container direction="column" spacing={1}>
					{tags.map((tag, i) => {
						return (
							<Grid item key={`tag-${i}`}>
								<Typography color="brand.grey.500">{tag.name}</Typography>
							</Grid>
						);
					})}
				</Grid>
			),
		});
	}

	if (dateStart) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="date" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('startDate')}</Typography>
					</Grid>
				</Grid>
			),
			value: dateStart,
		});
	}

	if (dateEnd) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="date" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('endDate')}</Typography>
					</Grid>
				</Grid>
			),
			value: dateEnd,
		});
	}

	if (amountMin) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="payment" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('minAmount')}</Typography>
					</Grid>
				</Grid>
			),
			value: amountMin,
		});
	}

	if (amountMax) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle size="sm" type="payment" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('maxAmount')}</Typography>
					</Grid>
				</Grid>
			),
			value: amountMax,
		});
	}

	if (currency) {
		data.push({
			label: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<IconCircle color="grey" size="sm" type="payment" />
					</Grid>
					<Grid item>
						<Typography fontWeight={500}>{t('currency')}</Typography>
					</Grid>
				</Grid>
			),
			value: currency,
		});
	}

	return <TableInfo data={data} />;
};
